import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import {
  BusinessMachineCover,
  getBusinessMachineCoverLabel,
} from 'src/app/_enums/business/business-machine-cover.enum';
import {
  BusinessMachineGroup,
  getBusinessMachineGroupLabel,
} from 'src/app/_enums/business/business-machine-group.enum';
import {
  BUSINESS_WAITING_DAYS_VALUES,
  BusinessWaitingDays,
  getBusinessWaitingDaysLabel,
} from 'src/app/_enums/business/business-waiting-days.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';

import { LeadFormBase } from '../../lead-form-base';
import { TechnicalInstallationBasicCoverageFormComponent } from './basic-coverage-form/basic-coverage-form.component';

type BusinessTechnicalInstallationBasicCoverageFormGroup = {
  covered: FormControl<boolean | null>;
  insuredSum: FormControl<number | null>;
  deductible: FormControl<number | null>;
};

type BusinessMachineCoverageFormGroup = Omit<
  BusinessTechnicalInstallationBasicCoverageFormGroup,
  'covered'
> & {
  inventory: FormControl<string | null>;
  cover: FormControl<BusinessMachineCover | null>;
};

type BusinessMachineLossCoverageFormGroup =
  BusinessTechnicalInstallationBasicCoverageFormGroup & {
    machinesGroup: FormControl<BusinessMachineGroup | null>;
    namedMachines: FormControl<string | null>;
    coverageIn: FormControl<string | null>;
    waitingDelay: FormControl<BusinessWaitingDays | null>;
  };

type BusinessTechnicalInstallationLossCoverageFormGroup =
  BusinessTechnicalInstallationBasicCoverageFormGroup & {
    coverageIn: FormControl<string | null>;
    waitingDelay: FormControl<BusinessWaitingDays | null>;
  };

type BusinessTechnicalInstallationCoverageFormGroup =
  BusinessTechnicalInstallationBasicCoverageFormGroup & {
    inventory: FormControl<string | null>;
    replacementCostCoverage: FormControl<boolean | null>;
    internalOperatingDamageCoverage: FormControl<boolean | null>;
  };

type MachinesInsuranceMetadataFormGroup = {
  machinesInsured: FormControl<boolean | null>;
  firstTypeMachineCoverage: FormGroup<BusinessMachineCoverageFormGroup>;
  secondTypeMachineCoverage: FormGroup<BusinessMachineCoverageFormGroup>;
  lossCoverage: FormGroup<BusinessMachineLossCoverageFormGroup>;
  additionalFeesCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  complementaryCoverage: FormControl<boolean | null>;
  toolsMoldsCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  dataCarriersCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  furniturePiecesCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  additionalConstructionServicesCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  rescueCostsCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  internalDisturbancesDamageCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  undergroundInaccessibilityCoverage: FormControl<boolean | null>;
  fireNaturalDisasterCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  theftCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  replacementCostCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
};

type TechnicalInstallationInsuranceMetadataFormGroup = {
  technicalInstallationsInsured: FormControl<boolean | null>;
  computerObjectsCoverage: FormGroup<BusinessTechnicalInstallationCoverageFormGroup>;
  technicalInstallationCoverage: FormGroup<BusinessTechnicalInstallationCoverageFormGroup>;
  complementaryCoverage: FormControl<boolean | null>;
  dataCarriersCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  computerObjectsInSwitzerlandCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  computerObjectsInternationalCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  wiringCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  additionalConstructionServicesCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  computerObjectsAdditionalFeesCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  cyberCoverage: FormControl<boolean | null>;
  internalDisturbancesDamageCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  undergroundInaccessibilityCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  fireNaturalDisasterCoverageExcluded: FormControl<boolean | null>;
  theftCoverage: FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup>;
  lossCoverage: FormGroup<BusinessTechnicalInstallationLossCoverageFormGroup>;
  theftDamagesExcluded: FormControl<boolean | null>;
  waterDamagesExcluded: FormControl<boolean | null>;
};

@Component({
  selector: 'app-business-technical-installation-form',
  templateUrl: './business-technical-installation-form.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TechnicalInstallationBasicCoverageFormComponent,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
})
export class BusinessTechnicalInstallationFormComponent
  extends LeadFormBase
  implements OnDestroy, OnInit
{
  @Input()
  formGroup!: FormGroup;

  private subscriptions: Subscription[] = [];

  constructor(private fb: FormBuilder) {
    super();
  }

  createBasicCoverageFormGroup(): FormGroup<BusinessTechnicalInstallationBasicCoverageFormGroup> {
    return this.fb.group({
      covered: [null],
      insuredSum: [null],
      deductible: [null],
    });
  }

  createMachineCoverageFormGroup(): FormGroup<BusinessMachineCoverageFormGroup> {
    return this.fb.group({
      insuredSum: [null],
      deductible: [null],
      inventory: [null],
      cover: [null],
    });
  }

  createMachineLossCoverageFormGroup(): FormGroup<BusinessMachineLossCoverageFormGroup> {
    return this.fb.group({
      covered: [null],
      insuredSum: [null],
      deductible: [null],
      machinesGroup: [null],
      namedMachines: [null],
      coverageIn: [null],
      waitingDelay: [null],
    });
  }

  createTechnicalInstallationLossCoverageFormGroup(): FormGroup<BusinessTechnicalInstallationLossCoverageFormGroup> {
    return this.fb.group({
      covered: [null],
      insuredSum: [null],
      deductible: [null],
      coverageIn: [null],
      waitingDelay: [null],
    });
  }

  createTechnicalInstallationCoverageFormGroup(): FormGroup<BusinessTechnicalInstallationCoverageFormGroup> {
    return this.fb.group({
      covered: [null],
      insuredSum: [null],
      deductible: [null],
      inventory: [null],
      replacementCostCoverage: [null],
      internalOperatingDamageCoverage: [null],
    });
  }

  metadataFormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_TECHNICAL_INSTALLATION,
    machinesInsuranceMetadata: this.fb.group({
      machinesInsured: [null],
      firstTypeMachineCoverage: this.createMachineCoverageFormGroup(),
      secondTypeMachineCoverage: this.createMachineCoverageFormGroup(),
      lossCoverage: this.createMachineLossCoverageFormGroup(),
      additionalFeesCoverage: this.createBasicCoverageFormGroup(),
      complementaryCoverage: [null],
      toolsMoldsCoverage: this.createBasicCoverageFormGroup(),
      dataCarriersCoverage: this.createBasicCoverageFormGroup(),
      furniturePiecesCoverage: this.createBasicCoverageFormGroup(),
      additionalConstructionServicesCoverage:
        this.createBasicCoverageFormGroup(),
      rescueCostsCoverage: this.createBasicCoverageFormGroup(),
      internalDisturbancesDamageCoverage: this.createBasicCoverageFormGroup(),
      undergroundInaccessibilityCoverage: [null],
      fireNaturalDisasterCoverage: this.createBasicCoverageFormGroup(),
      theftCoverage: this.createBasicCoverageFormGroup(),
      replacementCostCoverage: this.createBasicCoverageFormGroup(),
    }),
    technicalInstallationInsuranceMetadata: this.fb.group({
      technicalInstallationsInsured: [null],
      computerObjectsCoverage:
        this.createTechnicalInstallationCoverageFormGroup(),
      technicalInstallationCoverage:
        this.createTechnicalInstallationCoverageFormGroup(),
      complementaryCoverage: [null],
      dataCarriersCoverage: this.createBasicCoverageFormGroup(),
      computerObjectsInSwitzerlandCoverage: this.createBasicCoverageFormGroup(),
      computerObjectsInternationalCoverage: this.createBasicCoverageFormGroup(),
      wiringCoverage: this.createBasicCoverageFormGroup(),
      additionalConstructionServicesCoverage:
        this.createBasicCoverageFormGroup(),
      computerObjectsAdditionalFeesCoverage:
        this.createBasicCoverageFormGroup(),
      cyberCoverage: [null],
      internalDisturbancesDamageCoverage: this.createBasicCoverageFormGroup(),
      undergroundInaccessibilityCoverage: this.createBasicCoverageFormGroup(),
      fireNaturalDisasterCoverageExcluded: [null],
      theftCoverage: this.createBasicCoverageFormGroup(),
      lossCoverage: this.createTechnicalInstallationLossCoverageFormGroup(),
      theftDamagesExcluded: [null],
      waterDamagesExcluded: [null],
    }),
  });

  override ngOnInit(): void {
    super.ngOnInit();

    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      this.metadataFormGroup.patchValue(this.metadata);
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    // Setup conditional field subscriptions
    this.setupConditionalFieldSubscriptions();
  }

  private setupConditionalFieldSubscriptions(): void {
    // Watch machinesInsured - reset all machinesInsuranceMetadata fields when false
    const machinesInsuredControl =
      this.machinesInsuranceMetadata.controls.machinesInsured;
    const machinesInsuredSub = machinesInsuredControl.valueChanges.subscribe(
      (value: boolean | string | null) => {
        if (value !== true && value !== 'true') {
          // Reset all fields except machinesInsured itself
          this.machinesInsuranceMetadata.controls.firstTypeMachineCoverage.reset();
          this.machinesInsuranceMetadata.controls.secondTypeMachineCoverage.reset();
          this.machinesInsuranceMetadata.controls.lossCoverage.reset();
          this.machinesInsuranceMetadata.controls.additionalFeesCoverage.reset();
          this.machinesInsuranceMetadata.controls.complementaryCoverage.reset();
          this.machinesInsuranceMetadata.controls.toolsMoldsCoverage.reset();
          this.machinesInsuranceMetadata.controls.dataCarriersCoverage.reset();
          this.machinesInsuranceMetadata.controls.furniturePiecesCoverage.reset();
          this.machinesInsuranceMetadata.controls.additionalConstructionServicesCoverage.reset();
          this.machinesInsuranceMetadata.controls.rescueCostsCoverage.reset();
          this.machinesInsuranceMetadata.controls.internalDisturbancesDamageCoverage.reset();
          this.machinesInsuranceMetadata.controls.undergroundInaccessibilityCoverage.reset();
          this.machinesInsuranceMetadata.controls.fireNaturalDisasterCoverage.reset();
          this.machinesInsuranceMetadata.controls.theftCoverage.reset();
          this.machinesInsuranceMetadata.controls.replacementCostCoverage.reset();
        }
      }
    );
    this.subscriptions.push(machinesInsuredSub);

    // Watch technicalInstallationsInsured - reset all technicalInstallationInsuranceMetadata fields when false
    const technicalInstallationsInsuredControl =
      this.technicalInstallationInsuranceMetadata.controls
        .technicalInstallationsInsured;
    const technicalInstallationsInsuredSub =
      technicalInstallationsInsuredControl.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            // Reset all fields except technicalInstallationsInsured itself
            this.technicalInstallationInsuranceMetadata.controls.computerObjectsCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.technicalInstallationCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.complementaryCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.dataCarriersCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.computerObjectsInSwitzerlandCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.computerObjectsInternationalCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.wiringCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.additionalConstructionServicesCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.computerObjectsAdditionalFeesCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.cyberCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.internalDisturbancesDamageCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.undergroundInaccessibilityCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.fireNaturalDisasterCoverageExcluded.reset();
            this.technicalInstallationInsuranceMetadata.controls.theftCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.lossCoverage.reset();
            this.technicalInstallationInsuranceMetadata.controls.theftDamagesExcluded.reset();
            this.technicalInstallationInsuranceMetadata.controls.waterDamagesExcluded.reset();
          }
        }
      );
    this.subscriptions.push(technicalInstallationsInsuredSub);

    // Note: Basic coverage groups handle their own conditional logic in TechnicalInstallationBasicCoverageFormComponent

    // Loss coverage subscriptions
    const machineLossGroup =
      this.machinesInsuranceMetadata.controls.lossCoverage;
    const machineLossSub =
      machineLossGroup.controls.covered.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            machineLossGroup.controls.insuredSum.reset();
            machineLossGroup.controls.deductible.reset();
            machineLossGroup.controls.machinesGroup.reset();
            machineLossGroup.controls.namedMachines.reset();
            machineLossGroup.controls.coverageIn.reset();
            machineLossGroup.controls.waitingDelay.reset();
          }
        }
      );
    this.subscriptions.push(machineLossSub);

    const technicalLossGroup =
      this.technicalInstallationInsuranceMetadata.controls.lossCoverage;
    const technicalLossSub =
      technicalLossGroup.controls.covered.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            technicalLossGroup.controls.insuredSum.reset();
            technicalLossGroup.controls.deductible.reset();
            technicalLossGroup.controls.coverageIn.reset();
            technicalLossGroup.controls.waitingDelay.reset();
          }
        }
      );
    this.subscriptions.push(technicalLossSub);

    // Technical installation coverage subscriptions
    const computerObjectsGroup =
      this.technicalInstallationInsuranceMetadata.controls
        .computerObjectsCoverage;
    const computerObjectsSub =
      computerObjectsGroup.controls.covered.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            computerObjectsGroup.controls.insuredSum.reset();
            computerObjectsGroup.controls.deductible.reset();
            computerObjectsGroup.controls.inventory.reset();
            computerObjectsGroup.controls.replacementCostCoverage.reset();
            computerObjectsGroup.controls.internalOperatingDamageCoverage.reset();
          }
        }
      );
    this.subscriptions.push(computerObjectsSub);

    const technicalInstallationGroup =
      this.technicalInstallationInsuranceMetadata.controls
        .technicalInstallationCoverage;
    const technicalInstallationSub =
      technicalInstallationGroup.controls.covered.valueChanges.subscribe(
        (value: boolean | string | null) => {
          if (value !== true && value !== 'true') {
            technicalInstallationGroup.controls.insuredSum.reset();
            technicalInstallationGroup.controls.deductible.reset();
            technicalInstallationGroup.controls.inventory.reset();
            technicalInstallationGroup.controls.replacementCostCoverage.reset();
            technicalInstallationGroup.controls.internalOperatingDamageCoverage.reset();
          }
        }
      );
    this.subscriptions.push(technicalInstallationSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  // Enum values and labels
  machineCoverValues = Object.values(BusinessMachineCover);

  getBusinessMachineCoverLabel = getBusinessMachineCoverLabel;

  machineGroupValues = Object.values(BusinessMachineGroup);

  getBusinessMachineGroupLabel = getBusinessMachineGroupLabel;

  waitingDaysValues = BUSINESS_WAITING_DAYS_VALUES;

  getBusinessWaitingDaysLabel = getBusinessWaitingDaysLabel;

  // Typed getters for form groups
  get machinesInsuranceMetadata(): FormGroup<MachinesInsuranceMetadataFormGroup> {
    return this.metadataFormGroup.controls.machinesInsuranceMetadata;
  }

  get technicalInstallationInsuranceMetadata(): FormGroup<TechnicalInstallationInsuranceMetadataFormGroup> {
    return this.metadataFormGroup.controls
      .technicalInstallationInsuranceMetadata;
  }

  // Helper methods to get basic coverage form groups (for use in templates)
  getMachinesBasicCoverageGroup(
    key: keyof MachinesInsuranceMetadataFormGroup
  ): FormGroup {
    return this.machinesInsuranceMetadata.controls[key] as FormGroup;
  }

  getTechnicalBasicCoverageGroup(
    key: keyof TechnicalInstallationInsuranceMetadataFormGroup
  ): FormGroup {
    return this.technicalInstallationInsuranceMetadata.controls[
      key
    ] as FormGroup;
  }

  // Helper getters for template access
  get machinesInsuredValue(): boolean | string | null {
    return this.machinesInsuranceMetadata.controls.machinesInsured.value;
  }

  get technicalInstallationsInsuredValue(): boolean | string | null {
    return this.technicalInstallationInsuranceMetadata.controls
      .technicalInstallationsInsured.value;
  }

  get machineLossCoverageCoveredValue(): boolean | string | null {
    return this.machinesInsuranceMetadata.controls.lossCoverage.controls.covered
      .value;
  }

  get computerObjectsCoverageCoveredValue(): boolean | string | null {
    return this.technicalInstallationInsuranceMetadata.controls
      .computerObjectsCoverage.controls.covered.value;
  }

  get technicalInstallationCoverageCoveredValue(): boolean | string | null {
    return this.technicalInstallationInsuranceMetadata.controls
      .technicalInstallationCoverage.controls.covered.value;
  }

  get technicalLossCoverageCoveredValue(): boolean | string | null {
    return this.technicalInstallationInsuranceMetadata.controls.lossCoverage
      .controls.covered.value;
  }
}
